/**
 * API service layer for communicating with the backend.
 */
import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  TranscriptionResponse,
  TranslationResponse,
  SummarizationResponse,
  QuestionGenerationResponse,
  TopicExplorationResponse,
  TTSRequest,
  ChatbotRequest,
  ChatbotResponse,
  DictionaryTerm,
  DictionaryTermCreate,
  DictionaryTermUpdate,
  DictionaryAutoTranslateRequest,
  DictionaryAutoTranslateResponse,
  User,
  UserCreate,
  UserUpdate,
  UserLogin,
  Token,
  Conversation,
  ConversationCreate,
  ConversationWithMessages,
  ConversationMessage as ConversationMessageType,
} from '../types';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 Token added to request:', config.url, token.substring(0, 30) + '...');
    } else {
      console.log('⚠️ No token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, clear auth
      console.error('❌ 401 Unauthorized:', error.response?.data);
      console.error('Request URL:', error.config?.url);
      console.error('Request headers:', error.config?.headers);
      // Don't redirect if already on login page
      if (!window.location.pathname.includes('login')) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.data);
    } else if (error.request) {
      // Request made but no response received
      console.error('Network Error:', error.request);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Transcribe audio file to text.
 * @param audioFile - Audio file to transcribe
 * @param language - Language code (default: "zh")
 * @param format - Audio format (default: "wav")
 * @returns Transcription response
 */
export const transcribeAudio = async (
  audioFile: File,
  language: string = 'zh',
  format: string = 'wav'
): Promise<TranscriptionResponse> => {
  const formData = new FormData();
  formData.append('audio', audioFile);
  formData.append('language', language);
  formData.append('format', format);

  const response = await api.post<TranscriptionResponse>(
    '/api/v1/transcribe',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Translate text using LLM.
 * @param text - Text to translate
 * @param sourceLang - Source language code (default: "zh")
 * @param targetLang - Target language code (default: "en")
 * @param model - Optional LLM model identifier
 * @param provider - Optional translation provider: "openrouter" or "dashscope"
 * @param context - Optional context for the conversation (e.g., "Chinese CS course lecture")
 * @returns Translation response
 */
export const translateText = async (
  text: string,
  sourceLang: string = 'zh',
  targetLang: string = 'en',
  model?: string | null,
  provider?: string | null,
  context?: string | null
): Promise<TranslationResponse> => {
  const payload: {
    text: string;
    source_lang: string;
    target_lang: string;
    model?: string;
    provider?: string;
    context?: string;
  } = {
    text,
    source_lang: sourceLang,
    target_lang: targetLang,
  };

  if (model) {
    payload.model = model;
  }

  if (provider) {
    payload.provider = provider;
  }

  if (context && context.trim()) {
    payload.context = context.trim();
  }

  const response = await api.post<TranslationResponse>(
    '/api/v1/translate',
    payload
  );
  return response.data;
};

/**
 * Summarize conversation history using LLM.
 * Supports incremental summarization by appending to existing summary.
 * @param conversationHistory - Full conversation text to summarize
 * @param sourceLang - Source language code (default: "zh")
 * @param targetLang - Target language code (default: "en")
 * @param summaryLanguage - Optional language for summary output (defaults to targetLang)
 * @param model - Optional LLM model identifier
 * @param provider - Optional translation provider: "openrouter" or "dashscope"
 * @param context - Optional context for the conversation
 * @param existingSummary - Existing summary to append to (for incremental mode)
 * @param summarizedLength - Number of characters already summarized (for incremental mode)
 * @returns Summarization response
 */
export const summarizeConversation = async (
  conversationHistory: string,
  sourceLang: string = 'zh',
  targetLang: string = 'en',
  summaryLanguage?: string | null,
  model?: string | null,
  provider?: string | null,
  context?: string | null,
  existingSummary?: string | null,
  summarizedLength?: number | null
): Promise<SummarizationResponse> => {
  const payload: {
    conversation_history: string;
    source_lang: string;
    target_lang: string;
    summary_language?: string;
    model?: string;
    provider?: string;
    context?: string;
    existing_summary?: string;
    summarized_length?: number;
  } = {
    conversation_history: conversationHistory,
    source_lang: sourceLang,
    target_lang: targetLang,
  };

  if (summaryLanguage) {
    payload.summary_language = summaryLanguage;
  }

  if (model) {
    payload.model = model;
  }

  if (provider) {
    payload.provider = provider;
  }

  if (context && context.trim()) {
    payload.context = context.trim();
  }

  if (existingSummary && existingSummary.trim() && summarizedLength !== null && summarizedLength !== undefined && summarizedLength > 0) {
    payload.existing_summary = existingSummary;
    payload.summarized_length = summarizedLength;
  }

  const response = await api.post<SummarizationResponse>(
    '/api/v1/summarize',
    payload
  );
  return response.data;
};

/**
 * Generate learning questions from conversation summary.
 * @param conversationSummary - Summary text to generate questions from
 * @param conversationHistory - Full conversation history (optional)
 * @param sourceLang - Source language code (default: "zh")
 * @param targetLang - Target language code (default: "en")
 * @param questionLanguage - Optional language for questions output (defaults to targetLang)
 * @param numQuestions - Number of questions to generate (default: 5, max: 10)
 * @param model - Optional LLM model identifier
 * @param provider - Optional translation provider: "openrouter" or "dashscope"
 * @param context - Optional context for the conversation
 * @returns Question generation response
 */
export const generateQuestions = async (
  conversationSummary: string,
  conversationHistory?: string | null,
  sourceLang: string = 'zh',
  targetLang: string = 'en',
  questionLanguage?: string | null,
  numQuestions: number = 5,
  model?: string | null,
  provider?: string | null,
  context?: string | null
): Promise<QuestionGenerationResponse> => {
  const payload: {
    conversation_summary: string;
    conversation_history?: string;
    source_lang: string;
    target_lang: string;
    question_language?: string;
    num_questions: number;
    model?: string;
    provider?: string;
    context?: string;
  } = {
    conversation_summary: conversationSummary,
    source_lang: sourceLang,
    target_lang: targetLang,
    num_questions: Math.min(Math.max(1, numQuestions), 10),
  };

  if (conversationHistory && conversationHistory.trim()) {
    payload.conversation_history = conversationHistory.trim();
  }

  if (questionLanguage) {
    payload.question_language = questionLanguage;
  }

  if (model) {
    payload.model = model;
  }

  if (provider) {
    payload.provider = provider;
  }

  if (context && context.trim()) {
    payload.context = context.trim();
  }

  const response = await api.post<QuestionGenerationResponse>(
    '/api/v1/generate-questions',
    payload
  );
  return response.data;
};

/**
 * Explore a topic in detail.
 * @param topic - Topic or concept to explore
 * @param question - Related question (optional, for context)
 * @param conversationContext - Conversation context (optional)
 * @param targetLang - Target language code (default: "en")
 * @param model - Optional LLM model identifier
 * @param provider - Optional translation provider: "openrouter" or "dashscope"
 * @returns Topic exploration response
 */
export const exploreTopic = async (
  topic: string,
  question?: string | null,
  conversationContext?: string | null,
  targetLang: string = 'en',
  model?: string | null,
  provider?: string | null
): Promise<TopicExplorationResponse> => {
  const payload: {
    topic: string;
    question?: string;
    conversation_context?: string;
    target_lang: string;
    model?: string;
    provider?: string;
  } = {
    topic: topic.trim(),
    target_lang: targetLang,
  };

  if (question && question.trim()) {
    payload.question = question.trim();
  }

  if (conversationContext && conversationContext.trim()) {
    payload.conversation_context = conversationContext.trim();
  }

  if (model) {
    payload.model = model;
  }

  if (provider) {
    payload.provider = provider;
  }

  const response = await api.post<TopicExplorationResponse>(
    '/api/v1/explore-topic',
    payload
  );
  return response.data;
};

/**
 * Synthesize speech from text using TTS.
 * @param text - Text to synthesize
 * @param voice - Voice name (default: "Cherry")
 * @param languageType - Language type (default: "Chinese")
 * @param stream - Whether to stream the response (default: false)
 * @returns Audio blob data
 */
export const synthesizeSpeech = async (
  text: string,
  voice: string = 'Cherry',
  languageType: string = 'Chinese',
  stream: boolean = false
): Promise<Blob> => {
  const payload: TTSRequest = {
    text: text.trim(),
    voice,
    language_type: languageType,
    stream,
  };

  const response = await api.post(
    '/api/v1/tts',
    payload,
    {
      responseType: 'blob', // Important: receive binary audio data
    }
  );
  return response.data;
};

/**
 * Chat with AI Assistant.
 * @param request - Chatbot request with message and optional conversation history
 * @returns Chatbot response
 */
export const chatWithAssistant = async (request: ChatbotRequest): Promise<ChatbotResponse> => {
  const response = await api.post<ChatbotResponse>(
    '/api/v1/chatbot',
    request
  );
  return response.data;
};

/**
 * Dictionary API functions
 */
export const getDictionaryTerms = async (
  search?: string,
  category?: string,
  skip: number = 0,
  limit: number = 20
): Promise<{ items: DictionaryTerm[]; total: number; skip: number; limit: number }> => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (category) params.append('category', category);
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());
  
  const response = await api.get<{ items: DictionaryTerm[]; total: number; skip: number; limit: number }>(
    `/api/v1/dictionary?${params.toString()}`
  );
  return response.data;
};

export const getDictionaryTerm = async (id: number): Promise<DictionaryTerm> => {
  const response = await api.get<DictionaryTerm>(`/api/v1/dictionary/${id}`);
  return response.data;
};

export const createDictionaryTerm = async (term: DictionaryTermCreate): Promise<DictionaryTerm> => {
  const response = await api.post<DictionaryTerm>('/api/v1/dictionary', term);
  return response.data;
};

export const updateDictionaryTerm = async (
  id: number,
  term: DictionaryTermUpdate
): Promise<DictionaryTerm> => {
  const response = await api.put<DictionaryTerm>(`/api/v1/dictionary/${id}`, term);
  return response.data;
};

export const deleteDictionaryTerm = async (id: number): Promise<void> => {
  await api.delete(`/api/v1/dictionary/${id}`);
};

export const autoTranslateDictionaryTerm = async (
  request: DictionaryAutoTranslateRequest
): Promise<DictionaryAutoTranslateResponse> => {
  const response = await api.post<DictionaryAutoTranslateResponse>(
    '/api/v1/dictionary/auto-translate',
    request
  );
  return response.data;
};

export const getCategories = async (): Promise<string[]> => {
  const response = await api.get<string[]>('/api/v1/dictionary/categories');
  return response.data;
};

/**
 * Authentication API functions
 */
export const register = async (userData: UserCreate): Promise<User> => {
  const response = await api.post<User>('/api/v1/auth/register', userData);
  return response.data;
};

export const login = async (loginData: UserLogin): Promise<Token> => {
  const response = await api.post<Token>('/api/v1/auth/login-json', loginData);
  return response.data;
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await api.get<User>('/api/v1/auth/me');
  return response.data;
};

export const updateProfile = async (userData: UserUpdate): Promise<User> => {
  const response = await api.put<User>('/api/v1/auth/me', userData);
  return response.data;
};

export const logout = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
};

/**
 * Conversation API functions
 */
export const getConversations = async (skip: number = 0, limit: number = 50): Promise<Conversation[]> => {
  const response = await api.get<Conversation[]>(`/api/v1/conversations?skip=${skip}&limit=${limit}`);
  return response.data;
};

export const getConversation = async (conversationId: number): Promise<ConversationWithMessages> => {
  const response = await api.get<ConversationWithMessages>(`/api/v1/conversations/${conversationId}`);
  return response.data;
};

export const createConversation = async (data?: ConversationCreate): Promise<Conversation> => {
  const response = await api.post<Conversation>('/api/v1/conversations', data || {});
  return response.data;
};

export const updateConversationTitle = async (conversationId: number, title: string): Promise<Conversation> => {
  const response = await api.put<Conversation>(`/api/v1/conversations/${conversationId}?title=${encodeURIComponent(title)}`);
  return response.data;
};

export const deleteConversation = async (conversationId: number): Promise<void> => {
  await api.delete(`/api/v1/conversations/${conversationId}`);
};

export const generateConversationTitle = async (conversationId: number): Promise<Conversation> => {
  const response = await api.post<Conversation>(`/api/v1/conversations/${conversationId}/generate-title`);
  return response.data;
};

export default api;


